import React, {useContext, useMemo} from "react"
import {selectUnit} from "@formatjs/intl-utils"
import {useIntl as _useIntl, IntlContext} from "react-intl"
import {IntlCtx} from "./types"
import {intlOverridesCtx} from "./Provider"

/**
 * Context exposing Intl imperative apis for displaying strings and numbers such as `formatMessage`
 *
 * NOTE: please prefer using `Formatted*` components exposed by `@irati/utils/lang` wherever possible
 *
 * @see [react-intl](https://formatjs.github.io/docs/react-intl/api#useintl-hook)
 */
function buildIntlCtx(
  intl: ReturnType<typeof _useIntl>,
  overrides: NonNullable<ReturnType<typeof useContext<typeof intlOverridesCtx>>>
): IntlCtx {
  return {
    ...intl,
    formatMessage: overrides.intlOverrides?.(intl).formatMessage ?? intl.formatMessage,
    formatRelative: (timestamp: string) => {
      const valueMillis = Number(new Date(timestamp))
      const diff = valueMillis - Date.now()
      const {value, unit} = selectUnit(Date.now() + diff)
      return intl.formatRelativeTime(value, unit)
    }
  }
}

/**
 * Safe variant of `useIntl` that returns `null` when `IntlProvider` or `LangProvider`
 * is not present in the tree, instead of throwing. Use this in components that may
 * render before providers are mounted (e.g. error boundaries, loading states).
 */
export function useSafeIntl(): IntlCtx | null {
  const intl = useContext(IntlContext)
  const overrides = useContext(intlOverridesCtx)
  return useMemo(() => (intl && overrides ? buildIntlCtx(intl as any, overrides) : null), [intl, overrides])
}

export const useIntl = (): IntlCtx => {
  // NOTE: we strip down the exposed types api to help cut down on shenanigans
  const intl = _useIntl()
  const overrides = React.useContext(intlOverridesCtx)
  if (!overrides) throw new Error("intlOverridesCtx.Provider is missing from tree")
  // we may require more overrides, but right now this is to allow web to
  // pass in a custom formatMessage provided by phrase for creating an in
  // app editor. See usages of `LangProvider` for details
  return useMemo(() => buildIntlCtx(intl, overrides), [intl, overrides])
}
